import * as React from 'react'
import { ChevronDown, File, Play, SkipForward } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Progress } from '../../../components/ui/progress'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../../../components/ui/alert-dialog'
import { cn } from '../../../lib/utils'
import { JobDetailPanel } from '../components/JobDetailPanel'
import type { IngestJobKind } from '../util/mergeIngestJobs'
import { tabularConfigBuildPath, tabularJobPath } from '../../TabularIngestJobPage/route'
import { SOURCE_LABELS, LAUNCHABLE, defaultSub } from './constants'
import {
  EXTERNAL_HISTORY_LABELS,
  externalHistoryCredentials,
  externalHistoryMissingCredential,
  isExternalHistoryMappingPause,
  parseExternalHistorySources
} from './externalHistory'
import { ExternalHistoryMappingStudio } from './ExternalHistoryMappingStudio'
import { isExternalHistorySource } from './plan'
import { PlcUpdateStatusPanel } from './PlcUpdateStatus'
import { resolveSiteId } from './siteIds'
import { useHeadlessLaunch } from './useHeadlessLaunch'
import { WizardChrome } from './WizardChrome'
import type { SourceId, WizardLaunchStatus } from './types'
import type { IngestWizardApi } from './useIngestWizard'

interface StepRunProps {
  wizard: IngestWizardApi
}

function toJobKind(kind: (typeof LAUNCHABLE)[SourceId] | undefined): IngestJobKind | null {
  if (!kind || kind.kind === 'integration') return null
  return kind.kind
}

export function StepRun({ wizard }: StepRunProps) {
  const { state, siteName, editRunInputs, discardDraft } = wizard
  const launch = useHeadlessLaunch(wizard)
  const [confirmCancelOpen, setConfirmCancelOpen] = React.useState(false)
  // An external historian (PI, Canary) bound to existing tags pauses the run
  // for mapping approval once the tag sources it binds to have finished.
  const pausedHistoryProvider =
    isExternalHistoryMappingPause(launch.pauseReason) &&
    launch.pausedSourceId &&
    isExternalHistorySource(launch.pausedSourceId)
      ? launch.pausedSourceId
      : null
  const awaitingHistoryMapping = pausedHistoryProvider !== null
  const historyPrep = pausedHistoryProvider ? state.prep[pausedHistoryProvider] : undefined
  const historyLabels = pausedHistoryProvider
    ? EXTERNAL_HISTORY_LABELS[pausedHistoryProvider]
    : null
  const historyCredentials =
    pausedHistoryProvider && historyPrep
      ? externalHistoryCredentials(
          pausedHistoryProvider,
          historyPrep,
          state.sub[pausedHistoryProvider] ?? defaultSub(pausedHistoryProvider)
        )
      : null
  const missingHistoryCredential =
    pausedHistoryProvider && historyCredentials
      ? externalHistoryMissingCredential(pausedHistoryProvider, historyCredentials)
      : null
  const siteId = resolveSiteId({
    mode: state.mode,
    existingSiteId: state.existingSiteId,
    newSiteName: state.newSiteName || siteName
  })
  const queue = state.launchQueue
  const settledCount = queue.filter(
    (id) =>
      launch.statuses[id] === 'done' ||
      launch.statuses[id] === 'skipped' ||
      (state.launched.has(id) && launch.statuses[id] !== 'paused')
  ).length
  const started = state.queueStarted || launch.running || launch.paused
  const done =
    started && !launch.running && !launch.paused && queue.length > 0 && settledCount >= queue.length
  const pct = queue.length === 0 ? 100 : Math.round((settledCount / queue.length) * 100)

  const title = done
    ? 'Ingest finished'
    : historyLabels
      ? `Map ${historyLabels.historian} history`
      : launch.paused
        ? 'Ingest paused'
        : `Ingest for ${siteName || 'this site'}`
  const description = done
    ? 'All sources finished. Review results on the Ingest page.'
    : historyLabels
      ? `The SCADA / OPC tags are ready. Review their ${historyLabels.record} grammars and mappings, then continue the same ingest run.`
      : launch.paused
        ? 'A job failed or was cancelled. Fix the issue, then resume to retry that source, or skip it and continue.'
        : 'This run advances its sources in order. You can return to Ingest and start another run in parallel.'

  return (
    <WizardChrome
      eyebrow="Step 04 — Run"
      title={title}
      description={description}
      onBack={!launch.running && !done ? editRunInputs : undefined}
    >
      {queue.length > 0 ? (
        <div className="mb-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
              Overall
            </span>
            <span className="font-mono text-11 text-ink-3">
              {settledCount}/{queue.length}
            </span>
          </div>
          <Progress value={started ? pct : 0} className="mt-1.5 h-1.5" />
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        {queue.map((id, index) => (
          <SourceRow
            key={id}
            id={id}
            index={index}
            wizard={wizard}
            status={launch.statuses[id]}
            error={launch.errors[id]}
            note={launch.notesBySource[id]}
            jobIds={launch.jobIdsBySource[id] ?? []}
            next={sourceNext(id, state.prep[id], launch.jobIdsBySource[id] ?? [])}
          />
        ))}
      </div>

      {launch.commandError ? (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">
          {launch.commandError}
        </div>
      ) : null}

      {pausedHistoryProvider && historyPrep && missingHistoryCredential ? (
        <div className="mt-4 rounded-lg border border-line bg-bg-elev p-3">
          <label className="text-xs font-medium text-ink" htmlFor="external-history-credential">
            {missingHistoryCredential.label}
          </label>
          <p className="mt-1 text-xs text-ink-3">
            The coordinator retains its sealed credential, but secrets are never returned to the
            browser after a refresh.
          </p>
          <Input
            id="external-history-credential"
            type="password"
            autoComplete={
              missingHistoryCredential.field === 'password' ? 'current-password' : 'off'
            }
            className="mt-3"
            value={historyPrep.fields[missingHistoryCredential.field] ?? ''}
            onChange={(event) =>
              wizard.updatePrep(pausedHistoryProvider, {
                fields: { [missingHistoryCredential.field]: event.target.value }
              })
            }
          />
        </div>
      ) : null}

      {pausedHistoryProvider && historyPrep && historyCredentials && !missingHistoryCredential ? (
        <div className="mt-4 rounded-lg border border-line bg-bg-elev p-3">
          <ExternalHistoryMappingStudio
            provider={pausedHistoryProvider}
            siteId={siteId}
            branchId={launch.mappingBranchId}
            groupId={launch.groupId}
            baseUrl={historyPrep.fields.baseUrl ?? ''}
            credentials={historyCredentials}
            skipTlsVerify={!!historyPrep.checks.skipTlsVerify}
            nameFilter={historyPrep.fields.nameFilter}
            rootPath={historyPrep.fields.rootPath}
            historySources={parseExternalHistorySources(historyPrep.fields.historySources)}
            grammarsJson={historyPrep.fields.grammars ?? ''}
            manualLinksJson={historyPrep.fields.manualLinks ?? ''}
            suggestJobId={historyPrep.fields.suggestJobId}
            additionalContext={historyPrep.fields.additionalContext}
            onHistorySourcesChange={(historySources) =>
              wizard.updatePrep(pausedHistoryProvider, {
                fields: {
                  historySources: JSON.stringify(historySources),
                  mappingValid: 'false'
                }
              })
            }
            onGrammarsChange={(grammars) =>
              wizard.updatePrep(pausedHistoryProvider, { fields: { grammars } })
            }
            onManualLinksChange={(manualLinks) =>
              wizard.updatePrep(pausedHistoryProvider, { fields: { manualLinks } })
            }
            onSuggestJobIdChange={(suggestJobId) =>
              wizard.updatePrep(pausedHistoryProvider, { fields: { suggestJobId } })
            }
            onAdditionalContextChange={(additionalContext) =>
              wizard.updatePrep(pausedHistoryProvider, { fields: { additionalContext } })
            }
            onMappingValidChange={(valid) =>
              wizard.updatePrep(pausedHistoryProvider, {
                fields: { mappingValid: valid ? 'true' : 'false' }
              })
            }
          />
        </div>
      ) : null}

      <div className="mt-3 flex min-h-8 flex-wrap items-center justify-end gap-2 pb-3">
        {!started && !done ? (
          <Button
            type="button"
            size="sm"
            onClick={() => void launch.runQueue()}
            disabled={queue.length === 0}
          >
            <Play className="size-4" />
            Start ingest
          </Button>
        ) : null}
        {launch.paused && !awaitingHistoryMapping ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void launch.skipPausedAndResume()}
            >
              <SkipForward className="size-4" />
              Skip and continue
            </Button>
            <Button type="button" size="sm" onClick={() => void launch.resumeQueue()}>
              <Play className="size-4" />
              Resume
            </Button>
          </>
        ) : null}
        {awaitingHistoryMapping ? (
          <Button
            type="button"
            size="sm"
            onClick={() => void launch.resumeQueue()}
            disabled={
              missingHistoryCredential !== null || historyPrep?.fields.mappingValid !== 'true'
            }
          >
            <Play className="size-4" />
            Approve mapping and continue
          </Button>
        ) : null}
        {!done ? (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => setConfirmCancelOpen(true)}
          >
            Cancel
          </Button>
        ) : null}
        {done ? (
          <Button type="button" size="sm" onClick={() => discardDraft()}>
            Back to Ingest
          </Button>
        ) : null}
      </div>
      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop tracking this ingest run?</AlertDialogTitle>
            <AlertDialogDescription>
              The saved wizard inputs will be discarded. Jobs that already started continue and
              remain in the run history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel size="sm">Keep working</AlertDialogCancel>
            <AlertDialogAction
              size="sm"
              variant="destructive"
              onClick={() => {
                discardDraft()
              }}
            >
              Stop tracking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WizardChrome>
  )
}

function SourceRow({
  id,
  index,
  wizard,
  status,
  error,
  note,
  jobIds,
  next
}: {
  id: SourceId
  index: number
  wizard: IngestWizardApi
  status?: WizardLaunchStatus
  error?: string
  note?: string
  jobIds: string[]
  next?: string
}) {
  const [open, setOpen] = React.useState(false)
  const launchable = !!LAUNCHABLE[id]
  const prep = wizard.state.prep[id]
  const isActive = wizard.state.activeLaunchId === id || status === 'running'
  const resolved: WizardLaunchStatus | 'ready' | 'planned' = !launchable
    ? 'planned'
    : (status ?? (wizard.state.launched.has(id) ? 'done' : 'ready'))
  const jobKind = toJobKind(LAUNCHABLE[id])
  const isPlcUpdate = id === 'plcupdate'
  const canExpand = jobIds.length > 0 && !!jobKind && id !== 'tabular'

  const fileLabel =
    prep && prep.files.length > 1
      ? `${prep.files.length} files`
      : prep?.file?.name ||
        prep?.files[0]?.name ||
        prep?.fields.agentUrl ||
        prep?.fields.instanceName ||
        prep?.fields.connectionName ||
        prep?.fields.jdbcUrl ||
        'Connection / no file'

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-line bg-bg-elev">
      <button
        type="button"
        disabled={!canExpand}
        aria-expanded={canExpand ? open : undefined}
        onClick={() => {
          if (canExpand) setOpen((v) => !v)
        }}
        className={cn(
          'flex flex-col gap-1 px-3.5 py-3 text-left',
          canExpand ? 'cursor-pointer hover:bg-bg-sunk/60' : 'cursor-default'
        )}
      >
        <div className="flex items-center gap-3">
          <span className="shrink-0 font-mono text-11 text-ink-3">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-14 font-medium text-ink">{SOURCE_LABELS[id]}</div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-ink-3">
              {prep?.file || (prep?.files.length ?? 0) > 0 ? (
                <>
                  <File className="size-3 shrink-0" />
                  <span className="truncate font-mono">{fileLabel}</span>
                </>
              ) : (
                <span className="truncate">{fileLabel}</span>
              )}
            </div>
          </div>
          <StatusPill status={isActive ? 'running' : resolved} />
          {canExpand ? (
            <ChevronDown
              className={cn(
                'size-4 shrink-0 text-ink-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
                open && 'rotate-180'
              )}
            />
          ) : null}
        </div>
        {note ? <p className="pl-8 text-xs text-ink-3">{note}</p> : null}
        {error ? <p className="pl-8 text-xs text-st-down">{error}</p> : null}
      </button>

      {next ? (
        <Link to={next} className="px-3.5 pb-3 pl-11 text-xs text-st-info hover:underline">
          {prep?.fields.action === 'build' ? 'Open config' : 'Open job page'}
        </Link>
      ) : null}

      {isPlcUpdate && jobIds.length > 0 ? <PlcUpdateStatusPanel jobIds={jobIds} /> : null}

      {canExpand ? (
        <div
          data-open={open}
          className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-2 border-t border-line px-3.5 py-3">
              {jobIds.map((jobId) => (
                <div key={jobId} className="overflow-hidden rounded-md border border-line">
                  {jobIds.length > 1 ? (
                    <div className="border-b border-line bg-bg-sunk px-3 py-1.5 font-mono text-11 text-ink-3">
                      {jobId.slice(0, 8)}
                    </div>
                  ) : null}
                  <JobDetailPanel kind={jobKind!} id={jobId} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function sourceNext(
  id: SourceId,
  prep: IngestWizardApi['state']['prep'][SourceId],
  jobIds: string[]
) {
  if (id !== 'tabular' || !jobIds[0]) return undefined
  if (prep?.fields.action === 'build') {
    const configName = prep.fields.configName?.trim()
    return configName ? tabularConfigBuildPath(configName, jobIds[0]) : undefined
  }
  return tabularJobPath(jobIds[0])
}

function StatusPill({ status }: { status: WizardLaunchStatus | 'ready' | 'planned' }) {
  const map = {
    planned: { label: 'Not launchable', className: 'bg-st-idle-bg text-st-idle' },
    ready: { label: 'Ready', className: 'bg-st-info-bg text-st-info' },
    pending: { label: 'Queued', className: 'bg-st-info-bg text-st-info' },
    running: { label: 'Running…', className: 'bg-st-warn-bg text-st-warn' },
    done: { label: 'Done', className: 'bg-st-run-bg text-st-run' },
    error: { label: 'Failed', className: 'bg-st-down-bg text-st-down' },
    paused: { label: 'Paused', className: 'bg-st-warn-bg text-st-warn' },
    skipped: { label: 'Skipped', className: 'bg-st-idle-bg text-st-idle' }
  } as const
  const m = map[status]
  return (
    <Badge variant="secondary" className={`border-transparent ${m.className}`}>
      {m.label}
    </Badge>
  )
}
